import {
   Stack,
   ContentCard,
   TextInput,
   Grid,
   Button,
   Group,
} from '@forwardflow/ui-v2'
import { ArrowLeftMdIcon, ChevronRightIcon, DataIcon } from '@forwardflow/icons'
import { motion } from 'framer-motion'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { useState, useEffect } from 'react'
import { OnboardingV2Service } from '@forwardflow/services/onboardingV2.service'
import { CheckCircle02 } from '@forwardflow/icons/CheckCircle02'
import { DEV_DUMMY, isFreeFlow, useDevTools } from '../dev/devTools'

export const FubConnect = () => {
   const [fubApiKey, setFubApiKey] = useState('')
   const { getNextStep, getPreviousStep, onboardingData, setIsFUBConnected } =
      useOnboardingStoreV2()
   const { organizationId, isFUBConnected } = onboardingData || {}
   const [isLoading, setIsLoading] = useState(false)
   const [error, setError] = useState<string | null>(null)
   const [isVerifying, setIsVerifying] = useState(false)
   const [isValid, setIsValid] = useState(false)

   // Dev tools (hidden bar): paste the dev key from VITE_APP_DEV_FUB_API_KEY.
   useDevTools({ fill: () => setFubApiKey(DEV_DUMMY.fubApiKey) })

   // Debounced validation of FUB API key
   useEffect(() => {
      setIsValid(false)
      setError(null)

      // Free flow (dev tools): no verification call.
      if (isFreeFlow()) {
         setIsValid(true)
         setIsVerifying(false)
         return
      }

      if (!fubApiKey) {
         setIsVerifying(false)
         return
      }

      setIsVerifying(true)

      const timeoutId = setTimeout(async () => {
         try {
            const response = await OnboardingV2Service.verifyFubApiKey({
               token: fubApiKey,
            })
            // Only the key itself has to be valid. FUB owner status is not
            // required to connect — non-owner users (team admins, assistants)
            // hold perfectly good API keys and were previously blocked from
            // finishing onboarding entirely.
            if (response?.data?.isCrmApiKeyValid === true) {
               setIsValid(true)
               setError(null)
            } else if (response?.data?.isCrmApiKeyValid === false) {
               setError('Invalid API key')
            }
         } catch (err) {
            setError('Failed to verify API key')
         } finally {
            setIsVerifying(false)
         }
      }, 500)

      return () => clearTimeout(timeoutId)
   }, [fubApiKey])

   const handleSubmitFubApiKey = async () => {
      // Free flow (dev tools): connect without a key or a backend call.
      if (isFreeFlow()) {
         setIsFUBConnected(true)
         getNextStep()
         return
      }
      if (!fubApiKey || !organizationId) {
         return
      }
      try {
         setIsLoading(true)
         const response = await OnboardingV2Service.submitFubApiKey({
            organizationId,
            token: fubApiKey,
         })
         if (response?.success) {
            setIsFUBConnected(true)
         }
      } catch (error) {
         console.error(error)
         setError('Failed to connect Follow Up Boss')
      } finally {
         setIsLoading(false)
      }
   }

   return (
      <Stack gap={32}>
         <ContentCard
            className="space-y-2"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
         >
            <p className="text-white font-medium">
               Perfect! Now let's connect your Follow Up Boss account.
            </p>
            <p className="text-[#99A1AF] text-sm font-medium max-w-[600px]">
               I'll need your API key to sync your data. Don't worry, your first
               sync will complete in the background over the next 24 hours.
            </p>
         </ContentCard>

         {!isFUBConnected ? (
            <ContentCard
               initial={{ opacity: 0, y: 30 }}
               animate={{ opacity: 1, y: 0 }}
               transition={{ duration: 0.3, delay: 0.3 }}
               className="space-y-6"
            >
               <p className="text-white/90 font-medium">
                  Follow Up Boss API Key
               </p>
               <div className="relative">
                  <TextInput
                     error={error}
                     shouldLeftSectionBeFocused
                     leftSection={<DataIcon color="#99A1AF" width={20} />}
                     placeholder="Paste your API key here..."
                     value={fubApiKey}
                     onChange={(e) => setFubApiKey(e.target.value)}
                  />
                  {fubApiKey && (
                     <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium">
                        {isVerifying && (
                           <span className="text-[#99A1AF]">Verifying...</span>
                        )}
                        {!isVerifying && isValid && (
                           <span className="text-[#00D492]">Verified</span>
                        )}
                     </div>
                  )}
               </div>
            </ContentCard>
         ) : (
            <ContentCard
               initial={{ opacity: 0, y: 30 }}
               animate={{ opacity: 1, y: 0 }}
               transition={{ duration: 0.3 }}
               className="rounded-2xl border border-[#00BC7D]/30 bg-[rgba(0,188,125,0.05)]"
            >
               <Group>
                  <CheckCircle02 color="#00D492" width={32} />
                  <Stack gap={2}>
                     <p className="text-[#D0FAE5]/90 font-medium">
                        Follow Up Boss connected
                     </p>
                     <p className="text-[#A4F4CF]/80 text-sm font-medium">
                        Syncing in background
                        <span className="animate-pulse">...</span>
                     </p>
                  </Stack>
               </Group>
            </ContentCard>
         )}
         <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.6 }}
         >
            <Grid>
               <Grid.Col span={3}>
                  <Button
                     variant="secondary"
                     className="w-full"
                     onClick={() => getPreviousStep()}
                  >
                     <ArrowLeftMdIcon width={12} /> Back
                  </Button>
               </Grid.Col>
               <Grid.Col span={9}>
                  <Button
                     className="w-full"
                     onClick={() => {
                        if (isFUBConnected) {
                           getNextStep()
                           return
                        }
                        handleSubmitFubApiKey()
                     }}
                     disabled={
                        !isFreeFlow() &&
                        ((!isValid && !isFUBConnected) ||
                           !organizationId ||
                           isLoading ||
                           isVerifying)
                     }
                     loading={isLoading}
                  >
                     {isFUBConnected ? 'Next' : 'Connect Follow Up Boss'}{' '}
                     <ChevronRightIcon width={18} />
                  </Button>
               </Grid.Col>
            </Grid>
         </motion.div>
      </Stack>
   )
}
